import { useState } from "react";
import { toast } from "react-hot-toast";

const useApiHandler = () => {
  const [loading, setLoading] = useState(false);

  const callApi = async (
    apiFunc,
    { onSuccess, onError, successMessage, errorMessage, finallyCallback, silent } = {}
  ) => {
    let toastId;
    try {
      if (!silent) setLoading(true);

      const response = await apiFunc();

      if (response) {
        if (!silent) console.log("successful");

        if (onSuccess) onSuccess(response);
        return response;
      } else {
        throw new Error("Invalid response received");
      }
    } catch (error) {
      let errorMsg = null;

      // Check if this is a token expiration error - handle both old and new formats
      const isTokenExpired =
        (error?.response?.status === 403 &&
          error?.response?.data?.detail === "Invalid token.") ||
        (error?.response?.status === 401 &&
          error?.response?.data?.code === "token_not_valid");

      if (error?.response?.data) {
        const data = error.response.data;
        // Check multiple possible error message fields
        errorMsg =
          data.error ||
          data.message ||
          data.detail ||
          (Array.isArray(data.non_field_errors) ? data.non_field_errors[0] : data.non_field_errors) ||
          (Array.isArray(data.property_type) ? data.property_type[0] : data.property_type);

        // If still no errorMsg and data is an object, try to get the first field error
        if (!errorMsg && typeof data === "object" && data !== null && !Array.isArray(data)) {
          const keys = Object.keys(data);
          if (keys.length > 0) {
            const firstVal = data[keys[0]];
            errorMsg = Array.isArray(firstVal) ? firstVal[0] : (typeof firstVal === "string" ? firstVal : null);
          }
        }

        // Fallback to string representation
        if (!errorMsg && typeof data === "string") {
          errorMsg = data;
        }
      }

      // If no specific error message found, use the error message or fallback
      if (!errorMsg) {
        errorMsg = error?.message || errorMessage || "Something went wrong";
      }

      // Don't show toast for token expiration - navigation will handle it
      // Suppress toasts for background polling (silent)
      if (!silent && !isTokenExpired) {
        // Only show error toast if it's a real error, not a successful operation
        if (error?.response?.status >= 400) {
          toast.error(errorMsg);
        } else if (error?.message !== "Invalid response received") {
          toast.error(errorMsg);
        }
      }

      if (onError) onError(error);
      throw error;
    } finally {
      if (!silent) setLoading(false);
      if (toastId) toast.dismiss(toastId);
      if (finallyCallback) finallyCallback();
    }
  };

  return { callApi, loading };
};

export default useApiHandler;
